/**
 * src/utils/releaseFlags.js
 *
 * The ONE client-side definition of a release flag (RPDA-04). Twin of
 * azure-functions/shared/releaseFlags.js — nothing under src/ may require
 * from azure-functions/ at runtime, so the logic is duplicated and
 * releaseFlags.parity.test.js proves the two agree on every input. Change
 * one, change the other.
 *
 * Resolution (fixed, fail-closed — RFLG-B-002):
 *   unregistered key ⇒ OFF; complete tenant override ({ on, at, by } — a row of
 *   dbo.tenant_release_overrides, GH #8792; never the state blob) ⇒ its `on`;
 *   complete environment default ⇒ its `on`; a default never written (row
 *   read, no entry for the key) ⇒ the entry's mandatory `whenUnset`;
 *   anything else (unreadable defaults, incomplete record) ⇒ OFF. A bare
 *   boolean never counts.
 *
 * The browser never decides a release on its own: useReleaseFlag() shows a
 * module only when release-flags-read resolved it ON server-side. This twin
 * lets the super-admin Releases tab (RPDA-05) explain an effective state
 * (override vs inherited) with the same rules the server applies.
 */

/** Must equal the server's RELEASE_FLAGS minus each entry's server-only
 *  `recordProbe` — the parity test checks it. */
export const RELEASE_FLAGS = Object.freeze([
  {
    key: 'gapReportEditor',
    project: 'GAR',
    // The editor action is already live on production: registering must not hide it.
    whenUnset: 'on',
    description:
      'Gap analysis report: open the report in the document editor (create the linked document).',
  },
  {
    key: 'aiGovernance',
    project: 'AIG',
    // Already live on production: registering must not hide it (founder, 2026-10-27).
    whenUnset: 'on',
    description:
      'AI Governance: the register of AI systems, their Article 5 screenings, transfer impact assessments and risk-profile overrides.',
  },
  {
    key: 'communications',
    project: 'ACE',
    // Already live on production: registering must not hide it (founder, 2026-10-27).
    whenUnset: 'on',
    description:
      'Communications: My priorities (lens and depth, Explain, Ask about this), the ask-a-colleague loop and wrong-answer reports.',
  },
  {
    key: 'policyWorkflows',
    project: 'PDW',
    // Already live on production: registering must not hide it (founder, 2026-10-27).
    whenUnset: 'on',
    description:
      'Policy-driven workflows: the designer, run view, per-run assurance and the server-side workflow engine.',
  },
  {
    key: 'ismsImprovement',
    project: 'CI',
    // Already live on production: registering must not hide it (founder, 2026-10-27).
    whenUnset: 'on',
    description:
      'ISMS Improvement: the Metric Explorer, context changes, objectives and the executive index, with their metrics and registers.',
  },
  {
    key: 'leaderDialogue',
    project: 'LDG',
    whenUnset: 'on',
    description:
      'Leader dialogue — invite the top leader to a three-question dialogue from Management Review, and hand its gaps back as risk proposals and tasks.',
  },
  {
    key: 'roleCompetence',
    project: 'RCQ',
    // Already live on production: registering must not hide it (founder, 2026-10-27).
    whenUnset: 'on',
    description:
      'Role competence requirements (ISO 27001 7.2): required competence per role, competence assessments on employees and competence-gap tasks.',
  },
  {
    key: 'riskAiAssessment',
    project: 'RAI',
    // Live on production before the flag existed: unset ⇒ ON, so registering
    // never hides it (founder addendum 2026-10-27; read by the cutover resolver).
    whenUnset: 'on',
    description:
      'AI risk assessment: Assess with AI on the Risks page, the AI assessment log and its settings.',
  },
  {
    key: 'sourceConsistency',
    project: 'SCC',
    // Already live on production: registering must not hide it (founder, 2026-10-27).
    whenUnset: 'on',
    description:
      'Source consistency check — the Findings "Source consistency" register, its proposals and settings, and the management-review line.',
  },
]);

export const RELEASE_NAMESPACE = 'release';
const KEY_PATTERN = /^[a-z][A-Za-z0-9]{1,63}$/;
export const ENVIRONMENTS = Object.freeze(['registry-development', 'registry-test', 'registry']);
export const PRODUCTION_ENVIRONMENT = 'registry';

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isIsoTimestamp(v) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && !Number.isNaN(Date.parse(v));
}

export function isCompleteFlagRecord(rec) {
  return (
    isPlainObject(rec) &&
    typeof rec.on === 'boolean' &&
    isIsoTimestamp(rec.at) &&
    typeof rec.by === 'string' &&
    rec.by.trim().length > 0
  );
}

export function isValidFlagKey(key) {
  return typeof key === 'string' && KEY_PATTERN.test(key);
}

/** See the server twin: the two `whenUnset` answers, mandatory on every entry. */
export const WHEN_UNSET = Object.freeze(['on', 'off']);

export function isValidRegistryEntry(f) {
  return (
    isPlainObject(f) &&
    isValidFlagKey(f.key) &&
    typeof f.project === 'string' &&
    typeof f.description === 'string' &&
    WHEN_UNSET.includes(f.whenUnset)
  );
}

export function registryProblems(registry) {
  if (!Array.isArray(registry)) return ['registry is not an array'];
  const out = [];
  const seen = new Set();
  registry.forEach((f, i) => {
    const name = isPlainObject(f) && typeof f.key === 'string' ? f.key : `#${i}`;
    if (!isValidRegistryEntry(f)) {
      out.push(
        isPlainObject(f) && !WHEN_UNSET.includes(f.whenUnset)
          ? `${name}: whenUnset must be 'on' or 'off'`
          : `${name}: incomplete entry`
      );
    }
    if (seen.has(name)) out.push(`${name}: duplicate key`);
    seen.add(name);
  });
  return out;
}

export function findReleaseFlag(key, registry = RELEASE_FLAGS) {
  if (!isValidFlagKey(key) || !Array.isArray(registry)) return null;
  const f = registry.find((e) => e && e.key === key) || null;
  return f && isValidRegistryEntry(f) ? f : null;
}

/** `overrides` is the tenant's override map { <key>: { on, at, by } } (server-read). */
export function tenantOverride(overrides, key) {
  if (!isPlainObject(overrides)) return null;
  const rec = overrides[key];
  return isCompleteFlagRecord(rec) ? rec : null;
}

/**
 * True when the tenant's overrides cannot be READ: present but not an object.
 * "No override record" is different (⇒ environment default); overrides that
 * cannot be read are OFF — a release must never be inferred from a gap in
 * what we could read.
 */
export function isOverrideUnreadable(overrides) {
  if (overrides === undefined || overrides === null) return false;
  return !isPlainObject(overrides);
}

export function environmentDefault(defaults, key) {
  if (!isPlainObject(defaults)) return null;
  const flags = defaults.flags;
  if (!isPlainObject(flags)) return null;
  const rec = flags[key];
  return isCompleteFlagRecord(rec) ? rec : null;
}

/**
 * True when the environment default for `key` was never written: the defaults
 * row was READ (a plain object) and holds no entry for the key. Distinct from
 * unreadable (`defaults` null — a failed read, a missing table or row) and
 * from a present-but-incomplete record: both of those resolve OFF.
 */
export function isDefaultUnset(defaults, key) {
  if (!isPlainObject(defaults)) return false;
  const flags = defaults.flags;
  if (flags === undefined) return true;
  if (!isPlainObject(flags)) return false;
  return !Object.prototype.hasOwnProperty.call(flags, key);
}

/** @returns {{ on: boolean, source: 'tenant'|'environment'|'unset'|'off'|'unregistered' }} */
export function resolveReleaseFlag({
  overrides,
  environmentDefaults,
  key,
  registry = RELEASE_FLAGS,
  overrideUnreadable = false,
} = {}) {
  const flag = findReleaseFlag(key, registry);
  if (!flag) return { on: false, source: 'unregistered' };
  if (overrideUnreadable === true || isOverrideUnreadable(overrides))
    return { on: false, source: 'off' };
  const t = tenantOverride(overrides, key);
  if (t) return { on: t.on === true, source: 'tenant' };
  const e = environmentDefault(environmentDefaults, key);
  if (e) return { on: e.on === true, source: 'environment' };
  // RFLG-B-002: a bare or partly stamped override entry for this key never
  // takes the `whenUnset` answer — only NO entry does (it may still fall
  // through to a complete environment default, above).
  const strayOverride =
    isPlainObject(overrides) && Object.prototype.hasOwnProperty.call(overrides, key);
  if (strayOverride) return { on: false, source: 'off' };
  // Never written ⇒ the registry's declared answer; unreadable ⇒ OFF.
  if (isDefaultUnset(environmentDefaults, key))
    return { on: flag.whenUnset === 'on', source: 'unset' };
  return { on: false, source: 'off' };
}

/**
 * The flags the server resolved ON, from a release-flags-read response. A
 * tenant session receives only released flags, as `{ key }`; a super-admin
 * row carries `on` and counts only when it is exactly `true`. Fail closed: a
 * missing, malformed or failed response releases nothing.
 */
export function releasedKeys(response) {
  const out = new Set();
  const list = response && Array.isArray(response.flags) ? response.flags : [];
  for (const f of list) {
    if (!isPlainObject(f) || !isValidFlagKey(f.key)) continue;
    const hasOn = Object.prototype.hasOwnProperty.call(f, 'on');
    if (hasOn ? f.on === true : true) out.add(f.key);
  }
  return out;
}
